import { useCallback, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FeaturesService } from 'client/services';
import {
    mergeSupplementaryData,
    setSupplementaryDataLoading,
    setSupplementaryDataAssetError
} from 'client/store/SupplimentaryDataSlice/supplementaryDataSlice';

/**
 * Core lazy-fetch logic shared by useSupplementaryData and EnhancedMeasuresList — kept in one
 * place so the dedupe/dataProviderList/dispatch behaviour only needs to change in one spot.
 *
 * @returns {function(string[]): Promise<void>} fetchIds - filters to missing/not-in-flight ids,
 * builds the minimal dataProviderList and fetches + merges the result into the store
 */
export function useSupplementaryDataFetch () {
    const dispatch = useDispatch();
    const supplementaryData = useSelector(state => state.supplementaryData?.data, shallowEqual);
    const featureIdList = useSelector(state => state.supplementaryData?.featureIdList, shallowEqual);

    // Ref to track in-flight requests — prevents duplicate concurrent fetches for the same asset ID.
    // Using a ref (not state) because we don't need re-renders when the set changes.
    const fetchInFlight = useRef(new Set());

    const fetchIds = useCallback(async (assetIds) => {
        const missing = (assetIds || []).filter(id =>
            id && !supplementaryData?.[id] && !fetchInFlight.current.has(id)
        );
        if (!missing.length) {
            return;
        }

        // Build the dataProviderList from featureIdList for only the missing IDs.
        // featureIdList contains the full set from the AOI, and can list the same asset twice
        // (once as a regular feature, once as a sensitive feature), so we also dedupe by ID.
        const seenIds = new Set();
        const dataProviderList = (featureIdList || []).filter(item => {
            if (!missing.includes(item.dataProviderAssignedUniqueId) || seenIds.has(item.dataProviderAssignedUniqueId)) {
                return false;
            }
            seenIds.add(item.dataProviderAssignedUniqueId);
            return true;
        });

        if (!dataProviderList.length) {
            return;
        }

        // Mark assets as in-flight and dispatch loading state for UI feedback
        missing.forEach(id => fetchInFlight.current.add(id));
        dispatch(setSupplementaryDataLoading(missing));

        try {
            const result = await FeaturesService.getSupplementaryData(dataProviderList);
            // mergeSupplementaryData adds to existing store data rather than replacing it
            dispatch(mergeSupplementaryData(result));
        } catch (e) {
            // Per-asset error — allows granular retry without refetching everything
            dispatch(setSupplementaryDataAssetError({ assetIds: missing, error: e?.message || 'Supplementary data did not load' }));
        } finally {
            missing.forEach(id => fetchInFlight.current.delete(id));
        }
    }, [dispatch, featureIdList, supplementaryData]);

    return fetchIds;
}

/**
 * Shared hook for lazy-loading supplementary data on demand.
 *
 * Instead of fetching all supplementary data upfront on AOI draw, consumers call
 * this hook with the asset IDs they need. The hook:
 * 1. Checks the Redux store for already-loaded entries
 * 2. Filters to only the missing IDs (avoiding duplicate requests via an in-flight ref)
 * 3. Builds a minimal dataProviderList from the stored featureIdList
 * 4. Fetches and merges results incrementally into the store
 *
 * Used by: SelectFeaturePanel (single asset), RedactedAssetPanel (single asset).
 * EnhancedMeasuresList (group of assets, multiple ad-hoc subsets) uses the lower-level
 * useSupplementaryDataFetch directly, since it needs to fetch arbitrary subsets on demand
 * rather than a single fixed assetIds list.
 *
 * @param {string[]} assetIds - array of dataProviderAssignedUniqueId values to ensure are loaded
 * @returns {{ loading: boolean, hasError: boolean, errors: object, fetch: function, retryFailed: function }}
 */
export function useSupplementaryData (assetIds) {
    const dispatch = useDispatch();
    const loadingAssets = useSelector(state => state.supplementaryData?.loading, shallowEqual);
    const assetErrors = useSelector(state => state.supplementaryData?.assetErrors, shallowEqual);
    const fetchIds = useSupplementaryDataFetch();

    // Public fetch — fetchIds internally filters to only what's missing/not-in-flight
    const fetch = useCallback(() => fetchIds(assetIds), [fetchIds, assetIds]);

    // Retry only the specific failed IDs (clears their error state first)
    const retryFailed = useCallback((failedIds) => {
        dispatch(setSupplementaryDataAssetError({ assetIds: failedIds, error: null }));
        return fetchIds(failedIds);
    }, [dispatch, fetchIds]);

    // Compute loading state — true if any of the requested asset IDs are currently loading
    const loading = assetIds?.some(id => loadingAssets?.[id]) ?? false;

    // Compute error state — collect per-asset errors for only the requested IDs
    const errors = assetIds?.reduce((acc, id) => {
        if (assetErrors?.[id]) {
            acc[id] = assetErrors[id];
        }
        return acc;
    }, {}) ?? {};
    const hasError = Object.keys(errors).length > 0;

    return {
        loading,
        hasError,
        errors,
        fetch,
        retryFailed
    };
}
